import React from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { logClientError } from "@/lib/error-logger";
import { Sentry } from "@/lib/sentry";

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    // Report to Sentry
    Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
    logClientError({
      severity: "fatal",
      message: error.message || "Unknown render error",
      stack: error.stack,
      componentStack: errorInfo.componentStack || undefined,
    });
    toast({
      title: "Something went wrong",
      description: error.message || "An unexpected error occurred. Please try again.",
      variant: "destructive",
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="min-h-svh bg-[#070808] flex flex-col items-center justify-center p-8 md:p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-[#1c2020] border border-[#2a2f2f] flex items-center justify-center mb-6">
            <AlertTriangle className="h-7 w-7 text-[#ffb800]" />
          </div>
          <h2 className="text-[22px] font-semibold text-[#f5f5f5] mb-2 tracking-tight">
            Something went wrong
          </h2>
          <p className="text-[13px] text-[#9ca3a3] mb-8 max-w-sm leading-[1.6]">
            An unexpected error occurred. Your session data is safe. You can reload the page or return to the dashboard.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => window.location.reload()}
              className="bg-[#00e676] text-[#070808] rounded-[8px] px-5 py-2.5 text-[13px] font-semibold hover:bg-[#00ff85] transition-colors"
            >
              Reload page
            </button>
            <a
              href="/dash"
              className="bg-[#0e1111] border border-[#1c2020] text-[#f5f5f5] rounded-[8px] px-5 py-2.5 text-[13px] font-semibold hover:border-[#2a2f2f] hover:bg-[#141818] transition-colors"
            >
              Go to dashboard
            </a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
